import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Shell from "@/components/Shell";
import api, { formatINR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, CheckCircle2, Clock, XCircle, IndianRupee, TrendingUp } from "lucide-react";

const StatCard = ({ label, value, sub, icon: Icon, testid, accent = "text-zinc-950" }) => (
  <Card className="p-6 border-zinc-200 shadow-none rounded-md bg-white">
    <div className="flex items-center justify-between mb-3">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <Icon size={18} strokeWidth={1.5} className="text-zinc-400" />
    </div>
    <div className={`text-3xl font-extrabold tracking-tighter font-heading tabular-nums ${accent}`} data-testid={testid}>
      {value}
    </div>
    {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
  </Card>
);

export default function AdminDashboard() {
  const [overview, setOverview] = useState(null);
  const [team, setTeam] = useState([]);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/stats/overview").then((r) => setOverview(r.data)),
      api.get("/stats/team").then((r) => setTeam(r.data)),
      api.get("/invoices").then((r) => setRecent(r.data.slice(0, 5))),
    ]).catch(() => {});
  }, []);

  return (
    <Shell
      title="Team Overview"
      subtitle="Live view of pipeline, approvals and target progress."
      actions={
        <Link to="/admin/approvals">
          <Button className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="goto-approvals-button">
            Review Pending <ArrowUpRight size={16} className="ml-1" />
          </Button>
        </Link>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Total Invoice Value"
          value={formatINR(overview?.total_invoice_value || 0)}
          sub="Approved invoices · pre-GST"
          icon={IndianRupee}
          testid="stat-total-invoice"
        />
        <StatCard
          label="Total Margin"
          value={formatINR(overview?.total_margin || 0)}
          sub="Approved invoices"
          icon={TrendingUp}
          testid="stat-total-margin"
          accent="text-blue-700"
        />
        <StatCard
          label="Pending Approval"
          value={overview?.pending ?? 0}
          sub="Awaiting verification"
          icon={Clock}
          testid="stat-pending"
          accent="text-amber-700"
        />
        <StatCard
          label="Approved · Rejected"
          value={`${overview?.approved ?? 0} · ${overview?.rejected ?? 0}`}
          sub="Lifetime"
          icon={CheckCircle2}
          testid="stat-approved-rejected"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6 border-zinc-200 shadow-none rounded-md bg-white">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Target progress</div>
              <h2 className="text-xl font-bold tracking-tight font-heading">Team performance</h2>
            </div>
            <Link to="/admin/reports" className="text-sm font-semibold text-zinc-950 hover:underline" data-testid="link-reports">
              Full reports →
            </Link>
          </div>
          <div className="space-y-5">
            {team.length === 0 && (
              <div className="text-sm text-zinc-500 py-8 text-center border border-dashed border-zinc-200 rounded-md">
                No salespeople yet.{" "}
                <Link to="/admin/salespeople" className="font-semibold text-zinc-950 underline">
                  Add your first salesperson
                </Link>
              </div>
            )}
            {team.map((sp) => {
              const progress = sp.target_amount > 0 ? Math.min(sp.progress_pct, 100) : 0;
              const value = sp.target_type === "turnover" ? sp.attributed_invoice_value : sp.attributed_margin;
              return (
                <div key={sp.id} data-testid={`team-row-${sp.id}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-zinc-100 text-zinc-700 flex items-center justify-center text-xs font-bold">
                        {sp.name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-zinc-950">{sp.name}</div>
                        <div className="text-xs text-zinc-500 capitalize">{sp.target_type} target · {sp.deals_count} deals</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold tabular-nums">{formatINR(value)}</div>
                      <div className="text-xs text-zinc-500 tabular-nums">
                        of {formatINR(sp.target_amount)} · {sp.progress_pct}%
                      </div>
                    </div>
                  </div>
                  <Progress value={progress} className="h-1.5" />
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="p-6 border-zinc-200 shadow-none rounded-md bg-white">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Recent</div>
              <h2 className="text-xl font-bold tracking-tight font-heading">Latest invoices</h2>
            </div>
          </div>
          <div className="space-y-3">
            {recent.length === 0 && <div className="text-sm text-zinc-500">No invoices yet.</div>}
            {recent.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between gap-3 py-2 border-b last:border-b-0 border-zinc-100" data-testid={`recent-${inv.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{inv.customer_name}</div>
                  <div className="text-xs text-zinc-500">#{inv.invoice_no} · {inv.invoice_date?.slice(0, 10)}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-semibold tabular-nums">{formatINR(inv.invoice_value)}</div>
                  <StatusBadge status={inv.status} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </Shell>
  );
}

export function StatusBadge({ status }) {
  const map = {
    approved: { label: "Approved", icon: CheckCircle2, cls: "bg-blue-50 text-blue-700 border-blue-200" },
    pending: { label: "Pending", icon: Clock, cls: "bg-amber-50 text-amber-700 border-amber-200" },
    rejected: { label: "Rejected", icon: XCircle, cls: "bg-rose-50 text-rose-700 border-rose-200" },
  };
  const m = map[status] || map.pending;
  const Icon = m.icon;
  return (
    <Badge variant="outline" className={`gap-1 ${m.cls}`} data-testid={`status-${status}`}>
      <Icon size={12} strokeWidth={2} /> {m.label}
    </Badge>
  );
}
